import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Hub } from 'aws-amplify/utils';

import { ToastService } from '../../../core/services/toast.service';
import { SIGNIN_PATH } from '../auth.models';
import { AuthStore } from '../auth.store';

/**
 * 401 受信時のセッション切れ処理を集約するハンドラ。
 * inFlight フラグで並列 401 を 1 回に絞り、Toast / logout / fallback navigate を実行する。
 * logout 成功時の signin 遷移は Hub.signedOut → setupGlobalAuthListener が担当し、
 * 失敗時のみここで navigate(SIGNIN_PATH) する。
 *
 * 遷移前に開いているダイアログを全て閉じる。MatDialog の `closeOnNavigation`
 * （CDK の `disposeOnNavigation`）は `Location.subscribe` 経由でブラウザの
 * popstate / hashchange にしか反応しないため、Router による signin への遷移では
 * 閉じない。閉じずに遷移すると signin 画面の上に modal が残り、ユーザーが手で
 * 閉じるまで操作できなくなる。
 */
@Injectable({ providedIn: 'root' })
export class UnauthorizedHandlerService {
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly authStore = inject(AuthStore);
  private readonly dialog = inject(MatDialog);

  private inFlight = false;

  handle(): void {
    if (this.inFlight) {
      return;
    }
    this.inFlight = true;
    this.dialog.closeAll();
    this.toastService.showError(
      'セッションが切れました。再度ログインしてください。',
    );

    // logout() 内で signedOut が発火する前に listener を張る必要があるため、
    // logout() 呼び出しより先に Promise を作成しておく。
    const signedOut = this.waitForSignedOut();

    void this.authStore.logout().finally(() => {
      const fallback =
        this.authStore.error() !== null
          ? this.router.navigate([SIGNIN_PATH])
          : signedOut;
      void fallback.finally(() => {
        this.inFlight = false;
      });
    });
  }

  /**
   * signedOut Hub イベントの発火まで待つ。
   * setupGlobalAuthListener が signedOut を起点に navigate するため、
   * ここで signedOut まで inFlight を保持しておくことで短い間隔で再 401 が来た
   * 場合の Toast 重複を防ぐ。
   * タイムアウト（5 秒）は signedOut が何らかの理由で発火しない異常系のフォールバック。
   */
  private waitForSignedOut(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const cancel = Hub.listen('auth', ({ payload }) => {
        if (payload.event === 'signedOut') {
          cancel();
          clearTimeout(timer);
          resolve(true);
        }
      });
      const timer = setTimeout(() => {
        cancel();
        resolve(false);
      }, 5000);
    });
  }
}
